import { RoutesPaths } from "../../constants/routes"
import { calendarDeepLink } from "../../containers/Calendar/helpers/calendar-deep-link"
import {
  INBOX_TAB_PATH,
  inboxItemPath
} from "../../containers/Leads/ExternalInbox/constants/inbox-paths"
import { suggestionHref } from "../../containers/StatusAutomation/helpers/status-automation"
import type { ContentTarget } from "./notification-content.types"

/**
 * The route a content action opens.
 *
 * The backend names the record ("this invoice, on this project"); this turns
 * it into a path. Returns null when the target names nothing openable, so a
 * caller renders the action as text rather than promising a link that goes
 * nowhere.
 */
export function contentDestination(
  target?: ContentTarget | null
): string | null {
  if (!target) return null

  const project = target.project_id
  const lead = target.lead_id

  // A path the client resolved itself, for a row from a backend that predates
  // typed targets.
  if (target.kind === "legacy_href" && target.url) return target.url

  switch (target.kind) {
    case "project_finance":
      return project ? RoutesPaths.project.view(project).finance : null

    case "project_milestone":
      if (!project) return null
      return target.milestone_id
        ? `${RoutesPaths.project.view(project).milestones}?milestone=${target.milestone_id}`
        : RoutesPaths.project.view(project).milestones

    case "project_invoice":
      if (!project) return null
      return target.invoice_id
        ? `${RoutesPaths.project.view(project).invoices}?invoice=${target.invoice_id}`
        : RoutesPaths.project.view(project).invoices

    case "project_files":
      if (!project) return null
      return RoutesPaths.project.view(project).files({
        fileId: target.file_id,
        nav: target.folder_id
      })

    case "project_overview":
      return project ? RoutesPaths.project.view(project).overview : null

    case "lead":
      return lead
        ? RoutesPaths.lead.view(lead, {
            tab: target.folder_id ? "files" : undefined
          })
        : null

    case "lead_follow_up":
      // The follow-up lives on the lead's own page, in the schedule card the
      // Activity tab opens on.
      return lead ? RoutesPaths.lead.view(lead, { tab: "activity" }) : null

    case "inbox_item":
      return target.item_id ? inboxItemPath(target.item_id) : INBOX_TAB_PATH

    case "document_review":
      return target.review_id
        ? RoutesPaths.document_reviews.view(target.review_id)
        : null

    case "calendar_event":
      return target.event_id
        ? calendarDeepLink(undefined, target.event_id)
        : null

    case "conversation":
      return project
        ? RoutesPaths.project.view(project).messages({
            conversation: target.conversation_id,
            message: target.message_id
          })
        : null

    case "proposal":
      return target.proposal_id
        ? RoutesPaths.proposal.view(target.proposal_id)
        : null

    case "subcontractor":
      return target.subcontractor_id
        ? RoutesPaths.subcontractors.view(
            target.subcontractor_id,
            target.document
              ? { tab: "documents", document: target.document }
              : undefined
          )
        : null

    case "none":
    default:
      return null
  }
}

/**
 * The same, for a Next Best Action, whose params are a flat bag rather than a
 * typed target. `action` is the destination name the producer wrote; an older
 * feed carries `url` instead and is honoured as-is, and an item that names
 * its record only on itself (never in the params) still resolves.
 */
export function nbaDestination(
  params: Record<string, unknown>,
  item?: { project_id?: string | null; lead_id?: string | null }
): string | null {
  const read = (key: string) => {
    const value = params?.[key]
    if (typeof value === "string" && value) return value
    if (key === "lead_id" && item?.lead_id) return item.lead_id
    if (key === "project_id" && item?.project_id) return item.project_id
    return undefined
  }

  const url = read("url")
  if (url) return url

  const action = read("action")

  // The record's own page, where the suggestion waits in the header.
  if (action === "review_status_suggestion") {
    const project = read("project_id")
    const lead = read("lead_id")
    const suggestion = read("suggestion_id")
    if (project) return suggestionHref("project", project, suggestion)
    if (lead) return suggestionHref("lead", lead, suggestion)
    return null
  }

  const kindByAction: Record<string, string> = {
    review_milestone: "project_milestone",
    review_invoice: "project_invoice",
    review_project_finances: "project_finance",
    open_lead: "lead_follow_up",
    open_proposal: "proposal",
    review_compliance: "subcontractor"
  }

  if (action && kindByAction[action]) {
    return contentDestination({
      kind: kindByAction[action],
      project_id: read("project_id"),
      lead_id: read("lead_id"),
      milestone_id: read("milestone_id"),
      invoice_id: read("invoice_id"),
      proposal_id: read("proposal_id"),
      subcontractor_id: read("subcontractor_id"),
      document: read("document")
    })
  }

  // A feed produced before destinations were named still has to go somewhere
  // real rather than nowhere. `send_reminder` is the one older kind whose
  // record is a project: the reminder was about an invoice, and the project
  // is the nearest thing an un-named feed can open.
  if (action === undefined || !kindByAction[action]) {
    const project = read("project_id")
    const lead = read("lead_id")
    const kind =
      typeof params?.kind === "string" ? (params.kind as string) : undefined
    if (kind === "send_reminder" && project) {
      return RoutesPaths.project.view(project).overview
    }
    if (lead) return RoutesPaths.lead.view(lead)
    if (project) return RoutesPaths.project.view(project).overview
  }
  return null
}
